import { createContext, useContext, useState, useCallback, ReactNode, useEffect } from 'react';
import type { GrupoEmpresa, ConviteGrupo } from '@/mocks/admin/gruposEmpresas';
import {
  gruposEmpresas,
  convitesGrupo,
  getGrupoByEmpresaId,
  getEmpresasDoMesmoGrupo,
  getConvitesPendentes,
  getConvitesEnviados,
  criarGrupo,
  enviarConvite,
  responderConvite,
  removerEmpresaDoGrupo,
  dissolverGrupo,
  editarGrupo,
  cancelarConvite,
} from '@/mocks/admin/gruposEmpresas';

interface GrupoEmpresaContextType {
  grupoAtual: GrupoEmpresa | null;
  convitesPendentes: ConviteGrupo[];
  convitesEnviados: ConviteGrupo[];
  carregarDados: (empresaId: string) => void;
  criarNovoGrupo: (nome: string, descricao: string, criadoPor: string) => GrupoEmpresa;
  editarGrupoAtual: (nome: string, descricao: string) => GrupoEmpresa | null;
  convidarEmpresa: (grupoId: string, deEmpresaId: string, deEmpresaNome: string, paraEmpresaId: string) => ConviteGrupo;
  aceitarConvite: (conviteId: string) => ConviteGrupo | null;
  rejeitarConvite: (conviteId: string) => ConviteGrupo | null;
  cancelarConviteEnviado: (conviteId: string) => boolean;
  removerMembro: (grupoId: string, empresaId: string) => boolean;
  desfazerGrupo: (grupoId: string) => boolean;
  empresasDoGrupo: string[];
  empresasForaDoGrupo: string[];
}

const GrupoEmpresaContext = createContext<GrupoEmpresaContextType | undefined>(undefined);

const TODAS_EMPRESAS_IDS = ['EMP-001', 'EMP-002', 'EMP-003', 'EMP-004', 'EMP-005', 'EMP-006', 'EMP-007'];

export function GrupoEmpresaProvider({ children }: { children: ReactNode }) {
  const [grupoAtual, setGrupoAtual] = useState<GrupoEmpresa | null>(null);
  const [empresaAtual, setEmpresaAtual] = useState<string>('');
  const [convitesPendentes, setConvitesPendentes] = useState<ConviteGrupo[]>([]);
  const [convitesEnviados, setConvitesEnviados] = useState<ConviteGrupo[]>([]);

  const carregarDados = useCallback((empresaId: string) => {
    setEmpresaAtual(empresaId);
    const grupo = getGrupoByEmpresaId(empresaId);
    setGrupoAtual(grupo || null);
    const pendentes = getConvitesPendentes(empresaId);
    setConvitesPendentes(pendentes);
    const enviados = getConvitesEnviados(empresaId);
    setConvitesEnviados(enviados);
  }, []);

  const criarNovoGrupo = useCallback((nome: string, descricao: string, criadoPor: string) => {
    const novo = criarGrupo(nome, descricao, criadoPor);
    setGrupoAtual(novo);
    setConvitesPendentes([]);
    setConvitesEnviados([]);
    return novo;
  }, []);

  const editarGrupoAtual = useCallback((nome: string, descricao: string) => {
    if (!grupoAtual) return null;
    const resultado = editarGrupo(grupoAtual.id, nome, descricao);
    if (resultado) {
      setGrupoAtual({ ...resultado });
    }
    return resultado;
  }, [grupoAtual]);

  const convidarEmpresa = useCallback((grupoId: string, deEmpresaId: string, deEmpresaNome: string, paraEmpresaId: string) => {
    const convite = enviarConvite(grupoId, deEmpresaId, deEmpresaNome, paraEmpresaId);
    if (empresaAtual) {
      carregarDados(empresaAtual);
    }
    return convite;
  }, [empresaAtual, carregarDados]);

  const aceitarConvite = useCallback((conviteId: string) => {
    const resultado = responderConvite(conviteId, true);
    if (resultado && empresaAtual) {
      carregarDados(empresaAtual);
    }
    return resultado;
  }, [empresaAtual, carregarDados]);

  const rejeitarConvite = useCallback((conviteId: string) => {
    const resultado = responderConvite(conviteId, false);
    if (empresaAtual) {
      carregarDados(empresaAtual);
    }
    return resultado;
  }, [empresaAtual, carregarDados]);

  const cancelarConviteEnviado = useCallback((conviteId: string) => {
    const ok = cancelarConvite(conviteId);
    if (ok && empresaAtual) {
      carregarDados(empresaAtual);
    }
    return ok;
  }, [empresaAtual, carregarDados]);

  const removerMembro = useCallback((grupoId: string, empresaId: string) => {
    const ok = removerEmpresaDoGrupo(grupoId, empresaId);
    if (ok && empresaAtual) {
      carregarDados(empresaAtual);
    }
    return ok;
  }, [empresaAtual, carregarDados]);

  const desfazerGrupo = useCallback((grupoId: string) => {
    const ok = dissolverGrupo(grupoId);
    if (ok && empresaAtual) {
      carregarDados(empresaAtual);
    }
    return ok;
  }, [empresaAtual, carregarDados]);

  const empresasDoGrupo = grupoAtual ? getEmpresasDoMesmoGrupo(empresaAtual) : [];
  
  const empresasForaDoGrupo = TODAS_EMPRESAS_IDS.filter(
    id => id !== empresaAtual && !empresasDoGrupo.includes(id)
  );

  return (
    <GrupoEmpresaContext.Provider value={{
      grupoAtual,
      convitesPendentes,
      convitesEnviados,
      carregarDados,
      criarNovoGrupo,
      editarGrupoAtual,
      convidarEmpresa,
      aceitarConvite,
      rejeitarConvite,
      cancelarConviteEnviado,
      removerMembro,
      desfazerGrupo,
      empresasDoGrupo,
      empresasForaDoGrupo,
    }}>
      {children}
    </GrupoEmpresaContext.Provider>
  );
}

export function useGrupoEmpresa() {
  const context = useContext(GrupoEmpresaContext);
  if (context === undefined) {
    throw new Error('useGrupoEmpresa deve ser usado dentro de um GrupoEmpresaProvider');
  }
  return context;
}